import {
  Tooltip as MantineTooltip,
  TooltipProps as MantineTooltipProps,
} from '@mantine/core';
import { useMobileDetector } from '@zorro/web/ui-utils/hooks/useMobileDetector';
import { useCallback, useEffect, useState } from 'react';

import styles from './Tooltip.module.css';

export type TooltipProps = Omit<
  MantineTooltipProps,
  'color' | 'styles' | 'classNames' | 'style' | 'c'
> & {
  /** Stop event propagation on click */
  shouldStopPropagation?: boolean;
};

export const Tooltip = ({
  multiline = true,
  withArrow = true,
  arrowSize = 14,
  arrowOffset = 16,
  position = 'bottom',
  onMouseEnter,
  onMouseLeave,
  shouldStopPropagation = false,
  opened: controlledOpened,
  children,
  ...props
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useMobileDetector();

  const isControlled = controlledOpened !== undefined;
  const effectiveOpened = isControlled ? controlledOpened : isOpen;

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isControlled) {
        setIsOpen(isMobile ? true : !isOpen);
      }
      if (shouldStopPropagation) {
        event.stopPropagation();
      }
    },
    [isMobile, isOpen, shouldStopPropagation, isControlled]
  );

  // Close tooltip on scroll (mobile only)
  useEffect(() => {
    if (!isMobile || !isOpen || isControlled) return undefined;

    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isMobile, isOpen, isControlled]);

  return (
    <MantineTooltip
      unstyled
      classNames={{ tooltip: styles.tooltip, arrow: styles.arrow }}
      onMouseLeave={(args) => {
        if (!isControlled) {
          setIsOpen(false);
        }
        onMouseLeave?.(args);
      }}
      onMouseEnter={(args) => {
        if (!isControlled) {
          setIsOpen(true);
        }
        onMouseEnter?.(args);
      }}
      onClick={handleOnClick}
      withArrow={withArrow}
      arrowSize={arrowSize}
      arrowOffset={arrowOffset}
      multiline={multiline}
      position={position}
      opened={effectiveOpened}
      {...props}
    >
      {children}
    </MantineTooltip>
  );
};
